import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  CurrencyMappingTokenType,
  getMarketTrades,
  MarketTrades,
  SpotSymbol,
} from '../../../api/v1/market'
import { TokenSymbol } from '../../../types'
import { countDecimals } from '../../../utils/number'

export const getMarketTradesAction = createAsyncThunk<MarketTrades>(
  'market/getMarketTradesAction',
  async () => {
    const r = await getMarketTrades()
    const { data } = r

    data.spotsSymbols = data.spotsSymbols.filter((v) => v.name !== 'USDC_USDT')

    // Adding the baseCurrency to 'spotsSymbols' will enhance usability in the project
    // Because the 'baseName' in the SpotSymbol and the 'baseCurrency' in the PerpSymbol,
    // the difference key name will cause the project to be difficult to use.
    // So we add the 'baseCurrency' to the SpotSymbol.
    for (let i in data.spotsSymbols) {
      data.spotsSymbols[i].baseCurrency = (
        data.spotsSymbols[i] as SpotSymbol & { baseName: TokenSymbol }
      ).baseName
      data.spotsSymbols[i].quoteCurrency = (
        data.spotsSymbols[i] as SpotSymbol & { quoteName: TokenSymbol }
      ).quoteName
    }

    // Adding the Layer2CurrencyId to 'spotsSymbols' will enhance usability in the project
    for (let i in data.spotsSymbols) {
      const symbol = data.spotsSymbols[i]
      symbol.l2BaseCurrencyId =
        data.tokenMappingList.find(
          (v) =>
            v.tokenType === CurrencyMappingTokenType.Spot &&
            v.tokenName === symbol.baseCurrency,
        )?.mappingTokenId ?? 0
      symbol.l2QuoteCurrencyId =
        data.tokenMappingList.find(
          (v) =>
            v.tokenType === CurrencyMappingTokenType.Spot &&
            v.tokenName === symbol.quoteCurrency,
        )?.mappingTokenId ?? 0
    }

    // Adding the Layer2PerpId to 'spotsSymbols' will enhance usability in the project
    for (let i in data.contractsSymbols) {
      const symbol = data.contractsSymbols[i]
      symbol.baseScale = countDecimals(symbol.multiplier)
      symbol.layer2PerpId =
        data.tokenMappingList.find(
          (v) =>
            v.tokenType === CurrencyMappingTokenType.Perp &&
            v.tokenId === symbol.id,
        )?.mappingTokenId ?? 0
    }

    // To retrieve the corresponding tokenIcon and layer2CurrencyId from 'tokenMappingList'.
    for (let i in data.currencies) {
      const currency = data.currencies[i]
      const mapping = data.tokenMappingList.find(
        (v) => v.tokenId === currency.id,
      )
      currency.tokenIconUrl = mapping?.tokenIconUrl ?? ''
      currency.l2CurrencyId = mapping?.mappingTokenId ?? 0
      currency.l2Symbol = mapping?.mappingTokenName ?? ''
    }

    // Add the corresponding chain information for each token
    // Where the chain information is used for deposit and withdrawal purposes.
    for (let i in data.currencies) {
      const currency = data.currencies[i]
      const l2ChainInfo = data.chains[currency.l2CurrencyId]

      if (!l2ChainInfo) continue

      currency.chains = {}
      for (let n in l2ChainInfo) {
        currency.chains[l2ChainInfo[n].chainId] = l2ChainInfo[n]
      }
    }

    return data
  },
)
